import React, {Component, ReactElement, ReactNode} from 'react'

import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import NatDisasterRepo from '../repository/NatDisasterRepo'
import EventEmitterExAdmin from '../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {CommentInfo, SearchCondition, SearchResult} from '../repository/NatGetDisasterCommentList'
import {SortOrder} from '../../../../components/shared/SortOrder'
import {AdminPagePropsSet} from '../../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../../app/TypeHelpers'
import {
    AdminLoginInfoSessionStorageKey,
    NatAdminLoginInfoSessionStorageHelper
} from '../../../common/authentication/NatAdminLoginInfoSessionStorageHelper'
import {SessionStorageHelper} from '../../../../SessionStorageHelper'
import {ADMINISTORATOR_MODE, DISASTER_COMMENT_TYPE, DISASTER_TOPIC_CATEGORY} from '../../../../CodeMaster'
import {COMMON_CONSTANTS} from '../../../../CommonConstants'
import {NatGetCondominium} from '../../condominium/repository/NatGetCondominium'
import {Messages} from '../../../../Messages'
import {SearchResultCountDisplayProps} from '../../../../styledComponents/adminPage/SearchResultCountDisplay'
import {formatDateTimeForUpdateDate, formatForDateInput} from '../../../../formatters/formatDate'
import PPCheckboxGroup from '../../../../components/shared/forms/PPCheckboxGroup'
import SortableTableHeader, {
    SortableTableHeaderCell
} from '../../../../styledComponents/adminPage/SortablePaginationTableHeader'
import SortablePaginationTable from '../../../../styledComponents/adminPage/SortablePaginationTable'
import SortablePaginationTableBody from '../../../../styledComponents/adminPage/SortablePaginationTableBody'
import {Box, TableRow, TableCell, SortDirection, Link, Typography} from '@mui/material'
import {
    FullWidthLeftRowFlexContainer,
    LeftRowFlexContainer,
    RightRowFlexContainer,
    RowFlexContainer
} from '../../../../styledComponents/FlexComponents'
import PPDatePicker from '../../../../components/shared/forms/PPDatePicker'
import PPTextField from '../../../../components/shared/forms/PPTextField'
import ListSearchFormArea from '../../../../styledComponents/adminPage/ListSearchFormArea'
import {PageMainArea} from '../../../../components/shared/hoc/withAdminPageTemplate'
import styled from 'styled-components'
import {AdminDefaultMediumButton} from '../../../../styledComponents/adminPage/Buttons'
import {StyledDiv, StyledSpan} from '../../../../styledComponents/StyledTypeDefinitions'
import {HeadingAreaMessageParams} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import SearchPageActionArea from '../../../../styledComponents/adminPage/SearchPageActionArea'
import HintButton from '../../../../components/shared/pageBaseComponents/HintButton'
import AuthorityChecker from '../../../../components/AuthorityChecker'
import {HKConstants} from '../../../../HKConstants'
import {combineRidgeNameAndRoomNumber} from '../../../common/util/Natutil'
import {KEY_ADMIN_DISASTER_COMMENT_LIST_SEARCH_CONDITION} from '../../../../app/AdminSessionUtil'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    condominiumRepo: NatCondominiumRepo
    disasterRepo: NatDisasterRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    condominiumName: string
    commentInfo: CommentInfo[]
    searchResult: SearchResult
    searchCondition: SearchCondition
    searchConditionSortInfo: SortOrder[]
    noResultMessage: string
    isLoadCompleted: boolean
}

export default class NatAdminDisasterCommentListForm extends Component<Props & AdminPagePropsSet, States> {
    mode: Nullable<string>
    subSortKey: SortOrder = new SortOrder('commentId', 'asc')

    constructor(props: Props & AdminPagePropsSet) {
        super(props)
        this.mode = NatAdminLoginInfoSessionStorageHelper
            .getStringItemOrNull(AdminLoginInfoSessionStorageKey.SELECTED_MODE)

        // 検索条件の初期設定
        let defaultSearchCondition: SearchCondition = {}
        let lastSearchCondition: SearchCondition = {}

        // 前回検索条件が保持されていればデフォルト値として設定する
        const sessionStorageSearchCondition: string | null =
            SessionStorageHelper.getStringItemOrNull(KEY_ADMIN_DISASTER_COMMENT_LIST_SEARCH_CONDITION)

        if (sessionStorageSearchCondition) {
            lastSearchCondition = JSON.parse(sessionStorageSearchCondition)
        } else {
            // 前回検索条件が設定されていない場合、カテゴリーはすべてチェック状態とする
            defaultSearchCondition.category =
                DISASTER_TOPIC_CATEGORY.values().map(category => category.value).join(',')

            // 前回検索条件が設定されていない場合、コメントタイプはすべてチェック状態とする
            defaultSearchCondition.commentType =
                DISASTER_COMMENT_TYPE.values().map(commentType => commentType.value).join(',')
        }

        if (lastSearchCondition.pageNum) {
            lastSearchCondition.pageNum = Number(lastSearchCondition.pageNum)
            lastSearchCondition.page = COMMON_CONSTANTS.FIRST_PAGE
        } else {
            defaultSearchCondition.pageNum = COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[2]
            defaultSearchCondition.page = COMMON_CONSTANTS.FIRST_PAGE
        }

        if (Object.keys(lastSearchCondition).length) {
            defaultSearchCondition = {...lastSearchCondition}
        }

        this.state = {
            condominiumName: '',
            commentInfo: [],
            searchResult: {},
            searchCondition: defaultSearchCondition,
            searchConditionSortInfo: [
                new SortOrder('postDatetime', 'desc'),
                new SortOrder('ridgeName', 'asc'),
                new SortOrder('posterRoomNumber', 'asc'),
            ],
            noResultMessage: '',
            isLoadCompleted: false
        }

        this.handleChangePage = this.handleChangePage.bind(this)
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this)
        this.handleSortCommentList = this.handleSortCommentList.bind(this)
        this.onClickSearchButton = this.onClickSearchButton.bind(this)
        this.onClickExportDisasterCommentData = this.onClickExportDisasterCommentData.bind(this)
        this.onChangeStartPostDate = this.onChangeStartPostDate.bind(this)
        this.onChangeEndPostDate = this.onChangeEndPostDate.bind(this)
        this.onChangeKeyword = this.onChangeKeyword.bind(this)
        this.onChangeCategory = this.onChangeCategory.bind(this)
        this.onChangeCommentType = this.onChangeCommentType.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const {condominiumCd, condominiumRepo, eventEmitter} = this.props

            // 全体モードの場合は物件名を表示するために物件情報を取得する
            if (this.mode === ADMINISTORATOR_MODE.ALL.value) {
                const condominiumResponse: Nullable<NatGetCondominium> = await this.props.asyncCallFunction(
                    condominiumRepo.getCondominium.bind(condominiumRepo, condominiumCd),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (condominiumResponse?.details) {
                    this.setState({
                        condominiumName: condominiumResponse.details.condominiumName
                    })
                }
            }
            // 災害時コメント検索（管理）を取得
            await this.searchDisasterCommentList(
                this.state.searchCondition,
                this.state.searchConditionSortInfo
            )
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async searchDisasterCommentList(
        searchCondition: SearchCondition,
        searchConditionSortInfo: SortOrder[]
    ): Promise<void> {
        const {condominiumCd, disasterRepo, eventEmitter} = this.props
        await this.props.eventEmitter.showLoading()
        try {
            // 災害時コメント一覧を取得する
            const response = await this.props.asyncCallFunction(
                disasterRepo.searchDisasterCommentList.bind(
                    disasterRepo,
                    condominiumCd,
                    searchCondition, [...searchConditionSortInfo, this.subSortKey],
                ),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (response?.details) {
                this.setState({
                    commentInfo: response.details.commentInfo,
                    searchResult: response.details.searchResult,
                    noResultMessage:
                        response.details.searchResult.total === 0 ? Messages.IOPCC0014.toString('') : ''
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true, state: {}})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    searchResults(): SearchResultCountDisplayProps {
        return {
            totalCount: this.state.searchResult.total ? this.state.searchResult.total : undefined,
            rowsPerPage: this.state.searchCondition.pageNum,
            page: this.state.searchCondition.page,
        }
    }

    onChangeStartPostDate(date: Nullable<Date>): void {
        const searchCondition: SearchCondition = this.state.searchCondition;
        if (date !== null) {
            searchCondition.startPostDate = formatForDateInput(date.toString());
        } else {
            searchCondition.startPostDate = ''
        }
        this.setState({searchCondition: searchCondition})
    }

    onChangeEndPostDate(date: Nullable<Date>): void {
        const searchCondition: SearchCondition = this.state.searchCondition;
        if (date !== null) {
            searchCondition.endPostDate = formatForDateInput(date.toString());
        } else {
            searchCondition.endPostDate = ''
        }
        this.setState({searchCondition: searchCondition})
    }

    onChangeKeyword(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchConditions = this.state.searchCondition
        searchConditions.keyword = event.target.value
        this.setState({searchCondition: searchConditions})
    }

    async onClickSearchButton(): Promise<void> {
        this.beforeUserAction()
        // 検索条件を保持
        const lastSearchCondition: SearchCondition = {
            category: this.state.searchCondition.category,
            startPostDate: this.state.searchCondition.startPostDate,
            endPostDate: this.state.searchCondition.endPostDate,
            keyword: this.state.searchCondition.keyword,
            commentType: this.state.searchCondition.commentType,
            pageNum: this.state.searchCondition.pageNum,
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }
        SessionStorageHelper.setItem(
            KEY_ADMIN_DISASTER_COMMENT_LIST_SEARCH_CONDITION, JSON.stringify(lastSearchCondition))

        const searchCondition = this.state.searchCondition
        searchCondition.page = COMMON_CONSTANTS.FIRST_PAGE
        await this.searchDisasterCommentList(
            searchCondition,
            this.state.searchConditionSortInfo
        )
        this.setState({searchCondition: searchCondition})
    }

    async onClickExportDisasterCommentData(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const searchCondition: SearchCondition = {
                category: this.state.searchCondition.category,
                startPostDate: this.state.searchCondition.startPostDate,
                endPostDate: this.state.searchCondition.endPostDate,
                keyword: this.state.searchCondition.keyword,
                commentType: this.state.searchCondition.commentType
            }
            const tempLink: HTMLAnchorElement = document.createElement('a')
            tempLink.href = NatDisasterRepo.exportDisasterCommentData(this.props.condominiumCd, searchCondition)
            document.body.appendChild(tempLink)
            tempLink.click()
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    getSortDirection(sortKey: string): SortDirection {
        const sortOrder: SortOrder | undefined =
            this.state.searchConditionSortInfo.find(order => order.sortKey === sortKey)
        if (sortOrder === undefined) {
            return false
        }
        return sortOrder.sortDirection
    }

    async handleSortCommentList(sortKey: string): Promise<void> {
        this.beforeUserAction()

        let sortDirection: SortDirection = this.getSortDirection(sortKey)
        if (sortDirection === false || sortDirection === 'desc') {
            sortDirection = 'asc'
        } else {
            sortDirection = 'desc'
        }

        const sortOrders: SortOrder[] = [new SortOrder(sortKey, sortDirection)]
        await this.searchDisasterCommentList(
            this.state.searchCondition,
            sortOrders
        )
        this.setState({searchConditionSortInfo: sortOrders})
    }

    async handleChangePage(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): Promise<void> {
        this.beforeUserAction()
        const searchCondition: SearchCondition = this.state.searchCondition
        searchCondition.page = newPage
        await this.searchDisasterCommentList(
            searchCondition,
            this.state.searchConditionSortInfo
        )
        this.setState({searchCondition: searchCondition})
    }

    async handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.beforeUserAction()

        // 検索条件を保持
        const lastSearchCondition: SearchCondition = {
            category: this.state.searchCondition.category,
            startPostDate: this.state.searchCondition.startPostDate,
            endPostDate: this.state.searchCondition.endPostDate,
            keyword: this.state.searchCondition.keyword,
            commentType: this.state.searchCondition.commentType,
            pageNum: Number(event.target.value),
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }

        SessionStorageHelper.setItem(
            KEY_ADMIN_DISASTER_COMMENT_LIST_SEARCH_CONDITION, JSON.stringify(lastSearchCondition))

        const searchCondition: SearchCondition = this.state.searchCondition
        searchCondition.pageNum = Number(event.target.value)
        searchCondition.page = COMMON_CONSTANTS.FIRST_PAGE
        await this.searchDisasterCommentList(
            this.state.searchCondition,
            this.state.searchConditionSortInfo
        )
        this.setState({searchCondition: searchCondition})
    }

    handleCommentLinkClick(condominiumCd: string, topicId: number, commentId: number): void {
        this.props.navigate(
            `/admin/condominiums/${condominiumCd}/disasters/topics/${topicId}/comments/${commentId}`
        )
    }

    static getDisasterTopicCategory(categoryCode: string): string {
        const category = DISASTER_TOPIC_CATEGORY.codeToEnumConstants(categoryCode)
        if (category) {
            return category.label
        } else {
            return ''
        }
    }

    judgeCheckCategory(category: string): boolean {
        if (!this.state.searchCondition || !this.state.searchCondition.category) {
            return false
        }
        const setArray: string[] = this.state.searchCondition.category.split(',')
        return setArray.indexOf(category) >= 0
    }

    judgeCheckCommentType(commentType: string): boolean {
        if (!this.state.searchCondition || !this.state.searchCondition.commentType) {
            return false
        }
        const setArray: string[] = this.state.searchCondition.commentType.split(',')
        return setArray.indexOf(commentType) >= 0
    }

    onChangeCategory(label: string, checked: boolean): void {
        const checkedStatus = DISASTER_TOPIC_CATEGORY.values().filter(set => {
            return set.label === label
        })

        if (this.state.searchCondition.category) {
            const checkedStatusArray: string[] = this.state.searchCondition.category.split(',')
            if (checked) {
                checkedStatusArray.push(checkedStatus[0].value)
            } else {
                checkedStatusArray.splice(checkedStatusArray.indexOf(checkedStatus[0].value), 1)
            }
            const searchCondition = this.state.searchCondition
            searchCondition.category = checkedStatusArray.join(',')
            this.setState({searchCondition: searchCondition})
        } else {
            if (checked) {
                const searchCondition = this.state.searchCondition
                searchCondition.category = checkedStatus[0].value
                this.setState({searchCondition: searchCondition})
            }
        }
    }

    onChangeCommentType(label: string, checked: boolean): void {
        const checkedStatus = DISASTER_COMMENT_TYPE.values().filter(set => {
            return set.label === label
        })

        if (this.state.searchCondition.commentType) {
            const checkedStatusArray: string[] = this.state.searchCondition.commentType.split(',')
            if (checked) {
                checkedStatusArray.push(checkedStatus[0].value)
            } else {
                checkedStatusArray.splice(checkedStatusArray.indexOf(checkedStatus[0].value), 1)
            }
            const searchCondition = this.state.searchCondition
            searchCondition.commentType = checkedStatusArray.join(',')
            this.setState({searchCondition: searchCondition})
        } else {
            if (checked) {
                const searchCondition = this.state.searchCondition
                searchCondition.commentType = checkedStatus[0].value
                this.setState({searchCondition: searchCondition})
            }
        }
    }

    renderCategoryCheckBox(): ReactNode {
        const labels: { label: string, checked: boolean }[] = []
        const categoryMaster = DISASTER_TOPIC_CATEGORY.values()

        categoryMaster.map((category) => (
            labels.push(
                {
                    label: category.label,
                    checked: this.judgeCheckCategory(category.value),
                }
            )
        ))

        return (
            <StyledPPCheckboxGroup
                row
                data={labels}
                onChangeChecked={this.onChangeCategory}
            />
        )
    }

    renderCommentTypeCheckBox(): ReactNode {
        const labels: { label: string, checked: boolean }[] = []
        const commentTypeMaster = DISASTER_COMMENT_TYPE.values()

        commentTypeMaster.map((commentType) => (
            labels.push(
                {
                    label: commentType.label,
                    checked: this.judgeCheckCommentType(commentType.value),
                }
            )
        ))

        return (
            <StyledPPCheckboxGroup
                row
                data={labels}
                onChangeChecked={this.onChangeCommentType}
            />
        )
    }

    renderCommentListInfo(commentInfo: CommentInfo[]): Nullable<ReactElement> {
        if (commentInfo.length === 0) {
            return null
        }

        const headers: SortableTableHeaderCell[] = [];
        headers.push(
            {id: 'comment', label: 'コメント内容', align: 'left'},
            {id: 'topicCategory', label: 'カテゴリ', align: 'left'},
            {id: 'topicTitle', label: 'トピックタイトル', align: 'left'},
            {id: 'roomNumber', label: '部屋番号', align: 'left'},
            {id: 'posterName', label: '投稿者', align: 'left'},
            {id: 'replySource', label: '主コメント', align: 'left'},
            {
                id: 'postDatetime',
                label: 'コメント投稿日時',
                align: 'left',
                sortable: true,
                direction: this.getSortDirection('postDatetime'),
            }
        );
        return (
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.searchResult.total ? this.state.searchResult.total : 0,
                    rowsPerPage:
                        this.state.searchCondition.pageNum ? this.state.searchCondition.pageNum : 0,
                    page: this.state.searchCondition.page ? this.state.searchCondition.page : 0,
                    onPageChange: this.handleChangePage,
                    onRowsPerPageChange: this.handleChangeRowsPerPage,
                }}
            >
                <SortableTableHeader
                    noWrap
                    headers={headers}
                    onSortOrderChange={this.handleSortCommentList}
                />
                <SortablePaginationTableBody>
                    {commentInfo.map(
                        recode => this.renderCommentListInfoRecord(recode),
                    )}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        )
    }

    renderSearchArea(): ReactNode {
        const {condominiumName, searchCondition} = this.state
        return (
            <>
                <Box pl={'8px'}>
                    {this.mode === ADMINISTORATOR_MODE.ALL.value &&
                        <SearchRowContainer>
                            <SearchColumnContainer>物件名</SearchColumnContainer>
                            <SearchInputContainer>
                                {condominiumName}
                            </SearchInputContainer>
                        </SearchRowContainer>
                    }
                    <SearchRowContainer>
                        <SearchColumnContainer>カテゴリ</SearchColumnContainer>
                        <SearchInputContainer>
                            {this.renderCategoryCheckBox()}
                        </SearchInputContainer>
                    </SearchRowContainer>
                    <SearchRowContainer>
                        <SearchColumnContainer>コメント投稿日</SearchColumnContainer>
                        <SearchInputContainer>
                            <LeftRowFlexContainer>
                                <TermWrapper>
                                    <FlexBasis>
                                        <PPDatePicker
                                            onChangeDate={this.onChangeStartPostDate}
                                            value={(searchCondition?.startPostDate) ?
                                                new Date(searchCondition.startPostDate) : null
                                            }
                                            maxDate=
                                                {(searchCondition?.endPostDate)
                                                    ? new Date(searchCondition.endPostDate)
                                                    : undefined
                                                }
                                        />
                                    </FlexBasis>
                                    <TermTxt>から</TermTxt>
                                </TermWrapper>
                                <TermWrapper>
                                    <FlexBasis>
                                        <PPDatePicker
                                            onChangeDate={this.onChangeEndPostDate}
                                            value={(searchCondition?.endPostDate) ?
                                                new Date(searchCondition.endPostDate) : null
                                            }
                                            minDate={(searchCondition?.startPostDate) ?
                                                new Date(searchCondition.startPostDate) : undefined
                                            }
                                        />
                                    </FlexBasis>
                                    <TermTxt>まで</TermTxt>
                                </TermWrapper>
                            </LeftRowFlexContainer>
                        </SearchInputContainer>
                    </SearchRowContainer>
                    <SearchRowContainer>
                        <SearchColumnContainer style={{gap: '35px'}}> キーワード検索
                            <HintButton message={Messages.HDISC0001.message}/>
                        </SearchColumnContainer>
                        <SearchInputContainer>
                            <Box width={'500px'}>
                                <PPTextField
                                    fullWidth
                                    onChange={this.onChangeKeyword}
                                    defaultValue={searchCondition.keyword}
                                />
                            </Box>
                        </SearchInputContainer>
                    </SearchRowContainer>
                    <Box height={'8px'}/>
                    <RowFlexContainer>
                        <SearchColumnContainer>コメントタイプ</SearchColumnContainer>
                        <SearchInputContainer>
                            {this.renderCommentTypeCheckBox()}
                        </SearchInputContainer>
                        <Box flex={1} ml={'12px'}>
                            <RightRowFlexContainer>
                                <SearchButton onClick={this.onClickSearchButton}>
                                    検索する
                                </SearchButton>
                            </RightRowFlexContainer>
                        </Box>
                    </RowFlexContainer>
                </Box>
            </>
        )
    }

    renderCommentListInfoRecord(recode: CommentInfo): ReactElement {
        return (
            <TableRow key={recode.commentId}>
                <TableCell style={{width: '300px'}}>
                    <LinkOverflowWrapper>
                        <Link onClick={() => this.handleCommentLinkClick(
                            this.props.condominiumCd,
                            recode.topicId,
                            recode.commentId)}
                        >
                            <LinkOverflowEllipsis>
                                {recode.comment}
                            </LinkOverflowEllipsis>
                        </Link>
                    </LinkOverflowWrapper>
                </TableCell>
                <TableCell style={{width: '80px'}}>
                    {NatAdminDisasterCommentListForm.getDisasterTopicCategory(recode.topicCategory)}
                </TableCell>
                <TableCell style={{width: '250px'}}>
                    <TextOverflowEllipsis width={'226px'}>
                        {recode.topicTitle}
                    </TextOverflowEllipsis>
                </TableCell>
                <TableCellWordBreakAll style={{width: '120px'}}>
                    {combineRidgeNameAndRoomNumber(recode.posterRidgeName, recode.posterRoomNumber)}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll style={{width: '160px'}}>
                    {recode.posterFamilyName + ' ' + recode.posterFirstName}
                </TableCellWordBreakAll>
                <TableCell style={{width: '200px'}}>
                    <TextOverflowEllipsis width={'176px'}>
                        {recode.replySource}
                    </TextOverflowEllipsis>
                </TableCell>
                <TableCell>
                    {formatDateTimeForUpdateDate(recode.postDatetime)}
                </TableCell>
            </TableRow>
        )
    }

    render(): Nullable<ReactElement> {
        const {commentInfo, noResultMessage} = this.state

        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                <Box height={'10px'}/>
                <ListSearchFormArea
                    searchResult={this.searchResults()}
                    onEnterKey={this.onClickSearchButton}>
                    {this.renderSearchArea()}
                </ListSearchFormArea>
                <SearchPageActionArea>
                    <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.OUTPUT_DISASTER_INFORMATION_SHARING}>
                        <ButtonArea>
                            <AdminDefaultMediumButton onClick={this.onClickExportDisasterCommentData}>
                                コメントを抽出
                            </AdminDefaultMediumButton>
                        </ButtonArea>
                    </AuthorityChecker>
                </SearchPageActionArea>
                {commentInfo.length === 0
                    ? <Box mt={'10px'} ml={'10px'} mb={'14px'}>
                        <Typography variant={'body2'}>
                            {noResultMessage}
                        </Typography>
                    </Box>
                    : <PageMainArea>
                        {this.renderCommentListInfo(commentInfo)}
                    </PageMainArea>
                }
            </>
        )
    }
}

const SearchRowContainer = styled(FullWidthLeftRowFlexContainer)`
    padding: 8px 0;
`

const SearchInputContainer = styled.div`
    margin-left: 16px;
`

const SearchColumnContainer = styled(LeftRowFlexContainer)`
    width: 186px;
    flex-shrink: 0;
`

const SearchButton = styled(AdminDefaultMediumButton)`
    &.MuiButtonBase-root {
        width: 204px;
        height: 42px;
    }
`

const StyledPPCheckboxGroup = styled(PPCheckboxGroup)`
    & .MuiTypography-root {
        font-size: 14px;
    }
`

const TableCellWordBreakAll = styled(TableCell)`
    &.MuiTableCell-root {
        word-break: break-all;
    }
`

const LinkOverflowWrapper = styled.div`
    width: 276px;
`

const LinkOverflowEllipsis = styled.div`
    cursor: pointer;
    display: inline-block;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const TextOverflowEllipsis = styled.div<{ width: string }>`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    ${props => props.width ? `
    width:${props.width};
  ` : ''}
`

const FlexBasis: StyledDiv = styled.div`
    width: 334px;
`

const TermWrapper: StyledDiv = styled.div`
    display: flex;
    flex: 0 0 auto;
    align-items: flex-end;
`

const TermTxt: StyledSpan = styled.span`
    padding: 0 5px 0 5px;
`

const ButtonArea: StyledDiv = styled.div`
    display: flex;
    justify-content: flex-end;
`
